<script>
    import {
        LoadingModal,
        NotificationModal,
        SvelteTable,
    } from "../../../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../../../Services/index.svelte";

    import {
        getText,
        handleDownloadPDF,
        isObjectEmpty,
        showError,
        showException,
    } from "../../../../../Utils/utilFunctions.svelte";

    export let patientDetails = {},
        doctorId = Number(0),
        isUploadFileModalOpen = false,
        syncPatientFiles = () => {};

    $: isUploadBtnDisabled = isObjectEmpty(fileObj);

    $: if (isUploadFileModalOpen) setTimeout(() => enableFileDragAndDrop(), 0);
    else disableFileDragAndDrop();

    let fileObj = {},
        isUploadingFile = false,
        fileInput;

    let isDownloadingFile = false,
        isDeleteFileModalOpen = false,
        isDeletingFile = false;

    const columns = [
        {
            key: "fileName",
            title: "File Name",
            headerClass: "text-left pl-3 cursor-default w-[90%] rounded-tl-xl",
            class: "text-left px-3 cursor-default w-[90%]",
            renderValue: (v) => getText(v.fileName),
        },
        {
            key: "buttons",
            title: "",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) => _renderActionIcons(),
        },
    ];

    const enableFileDragAndDrop = () => {
        const enableDropArea = document.getElementById("enableDropArea");
        const uploadFileModal = document.getElementById("uploadFileModal");

        uploadFileModal.addEventListener("dragover", function (e) {
            e.preventDefault();

            enableDropArea.classList.add("outline-dashed");
            enableDropArea.classList.add("outline-secondary");
            enableDropArea.classList.add("outline-2");
        });

        uploadFileModal.addEventListener("dragleave", function (e) {
            enableDropArea.classList.remove("outline-dashed");
            enableDropArea.classList.remove("outline-secondary");
            enableDropArea.classList.remove("outline-2");
        });

        uploadFileModal.addEventListener("drop", function (e) {
            e.preventDefault();

            enableDropArea.classList.remove("outline-dashed");
            enableDropArea.classList.remove("outline-secondary");
            enableDropArea.classList.remove("outline-2");
        });

        enableDropArea.addEventListener("drop", function (e) {
            e.preventDefault();

            if (isUploadingFile) return;

            _handleFileChange(e, true);

            this.classList.remove("outline-dashed");
            this.classList.remove("outline-secondary");
            this.classList.remove("outline-2");
        });
    };

    const disableFileDragAndDrop = () => {
        const enableDropArea = document.getElementById("enableDropArea");

        enableDropArea &&
            enableDropArea.removeEventListener("drop", _handleFileChange);
    };

    const uploadFile = async () => {
        isUploadingFile = true;

        const document = {
            doctorId,
            entityId: patientDetails.id,
            fileName: fileObj.name,
            uploadedFileBase64: fileObj.content,
        };

        await apiMethods.postMethod
            .uploadPatientDocument(document)
            .then((res) => _handleModifyAction(res))
            .catch((err) => showException(err));

        _closeModal();

        isUploadingFile = false;
    };

    const downloadFile = async (data) => {
        isDownloadingFile = true;

        await apiMethods.getMethod
            .getFileById(data.id)
            .then((res) => _handleModifyAction(res, "download"))
            .catch((err) => showException(err));

        isDownloadingFile = false;
    };

    const deleteFile = async () => {
        isDeletingFile = true;

        await apiMethods.deleteMethod
            .deleteFileById(fileObj.id)
            .then((res) => _handleModifyAction(res, "delete"))
            .catch((err) => showException(err));

        _closeModal();

        isDeletingFile = false;
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "buttons") {
            if (className.includes("downloadIcon")) downloadFile(rowData);
            else if (className.includes("deleteIcon"))
                _openDeleteFileModal(rowData);
        }
    };

    // HELPER FUNCTIONS
    const _handleFileChange = (e, isDropFile = false) => {
        const inputField = document.getElementById("fileNamePlaceholder");

        inputField.innerText = isDropFile
            ? e.dataTransfer.files[0].name
            : e.target.files[0].name;

        const file = isDropFile ? e.dataTransfer.files[0] : e.target.files[0];
        const fileType = file?.type;
        const fileName = file?.name;

        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
            fileObj = {
                name: fileName,
                type: fileType,
                content: e.target.result.split(",")[1],
            };
        };
    };

    const _handleModifyAction = (res, type = "upload") => {
        if (!res || res.error)
            showError(
                `Failed to ${type} selected file, please refresh the page and try again and contact support if the issue persists.`,
            );
        else if (res.id) {
            if (type != "download")
                syncPatientFiles(patientDetails.patientEncounterId);
            else
                handleDownloadPDF(
                    res.fileContent.fileContents,
                    res.fileContent.fileDownloadName,
                    true,
                );
        }
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-center gap-x-2">
                    <div class="downloadIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Download Document">
                        <i class="downloadIcon fas fa-download"></i>
                    </div>

                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Document">
                        <i class="deleteIcon fa-solid fa-trash"></i>
                    </div>
                </div>`;
    };

    const _openDeleteFileModal = (data) => {
        fileObj = { ...data };
        isDeleteFileModalOpen = true;
    };

    const _closeModal = () => {
        isUploadFileModalOpen = false;
        isDeleteFileModalOpen = false;
        fileObj = {};

        const inputField = document.getElementById("fileNamePlaceholder");
        if (inputField) inputField.innerText = "";
        const fileInput = document.getElementById("fileInput");
        if (fileInput) fileInput.value = "";
    };
</script>

{#if !patientDetails.patientFiles?.length}
    <div
        class="flex flex-1 items-center justify-between px-2 py-4 w-full lg:flex-row"
    >
        <p>No documents currently under this patient</p>
    </div>
{:else if patientDetails.patientFiles.length}
    <div class="w-full overflow-x-auto">
        <SvelteTable
            {columns}
            rows={patientDetails.patientFiles}
            clickCellFunc={({ detail: { event, row, key } }) => {
                handleClickCell(event, row, key);
            }}
        />
    </div>
{/if}

<NotificationModal
    isModalOpen={isUploadFileModalOpen}
    title="Select file to upload"
    isConfirming={isUploadingFile}
    showCancelBtn={false}
    showConfirmBtn={false}
    closeModalFunction={_closeModal}
    customizeId="uploadFileModal"
    btnOptions={[
        {
            text: "Cancel",
            class: "btn btn-accent",
            loading: isUploadingFile,
            function: () => _closeModal(),
        },
        {
            text: isUploadingFile ? "Uploading" : "Upload",
            loading: isUploadingFile || isUploadBtnDisabled,
            function: () => uploadFile(),
        },
    ]}
>
    <div class="flex flex-col w-full gap-y-2" id="enableDropArea">
        <div class="flex flex-1 text-[#000]">
            <p
                id="fileNamePlaceholder"
                class="w-1/2 pt-2 pl-2 border-2 rounded-tl-lg rounded-bl-lg text-secondary"
            />
            <button
                class="w-1/2 p-2 rounded-tr-lg rounded-br-lg bg-[#E8E8E8] text-secondary {isUploadingFile
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer'}"
                on:click|preventDefault={() =>
                    !isUploadingFile && fileInput.click()}
            >
                Select or drag and drop here
            </button>
            <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic"
                style="display: none;"
                id="fileInput"
                on:change={_handleFileChange}
                bind:this={fileInput}
            />
        </div>
    </div>
</NotificationModal>

{#if isDownloadingFile}
    <LoadingModal titleText="Downloading...." />
{/if}

<NotificationModal
    isModalOpen={isDeleteFileModalOpen}
    title="Delete patient document"
    cancelText="Delete"
    submitText="Cancel"
    isReverseBtns
    confirmAction={_closeModal}
    closeModalFunction={_closeModal}
    closeModal={deleteFile}
    isConfirming={isDeletingFile}
>
    Delete patient document?
</NotificationModal>
